import * as React from "react";
import styled from "styled-components";
import { E2EIdentifier } from "../../../core/e2e/E2EIdentifier";
import { CloseButton } from "../common/CloseButton";
import * as helpers from "../../../ui/_themes/helpers";
import { bottomGradient, topGradient } from "../../../ui/_utils/styleUtils";

export interface IBetInfo {
  level: number;
  denomination: number;
  value: number;
}

export interface IBetInCashProps {
  className?: string;
  title: string;
  betsInfo: IBetInfo[];
  selectedBetInfo: IBetInfo;
  formatter(bet: number): string;
  onBetInfoChanged(info: IBetInfo): void;
  onClose(): void;
}

const View = ({
  className,
  title,
  betsInfo,
  selectedBetInfo,
  formatter,
  onBetInfoChanged,
  onClose
}: IBetInCashProps) => {
  const onBetClick = (info: IBetInfo) => () => {
    onBetInfoChanged(info);
  };

  return (
    <div className={className} data-e2e-id={E2EIdentifier.BET_VIEW_IN_CASH}>
      <div>
        <CloseButton onClose={onClose} />
        <h1>{title}</h1>
        <ul>
          {betsInfo.map((info, key) => (
            <List
              key={key}
              onClick={onBetClick(info)}
              data-active={info.value === selectedBetInfo.value}
            >
              {formatter(info.value)}
            </List>
          ))}
        </ul>
      </div>
    </div>
  );
};

const List = styled.li`
  text-align: center;
  cursor: pointer;
  user-select: none;
  font-size: 1.4em;
  font-weight: bold;
  border: 0.1em solid
    ${helpers.getColor("settingButtonBorder", "settingButton.border")};
  padding: 0.4em 1.8em;
  border-radius: 99em;
  background-color: ${helpers.getColor(
    "settingButtonBackground",
    "settingButton.background"
  )};
  color: ${helpers.getColor("settingButton")};
  -webkit-tap-highlight-color: transparent;

  /* No support for grid - IE 11 */
  margin: 0.6em 0.8em;
  width: 22.3%;

  @supports (display: grid) {
    margin: 0;
    width: 60%;
    max-width: 15em;
  }

  @media (hover: hover) {
    &:hover {
      border: 0.1em solid
        ${helpers.getHoverColor("settingButtonBorder", "settingButton.border")};
      background: ${helpers.getHoverColor(
        "settingButtonBackground",
        "settingButton.background"
      )};
      color: ${helpers.getHoverColor("settingButton")};
    }
  }

  &[data-active="true"] {
    border: 0.1em solid
      ${helpers.getSelectedColor("settingButtonBorder", "settingButton.border")};
    background-color: ${helpers.getSelectedColor(
      "settingButtonBackground",
      "settingButton.background"
    )};
    color: ${helpers.getSelectedColor("settingButton")};
    cursor: default;

    touch-action: none;
    pointer-events: none;
    user-select: none;
  }

  @media (min-width: 48em) and (max-width: 80em) {
    font-size: 1em;
    padding: 0.8em 0;
    width: 90%;
  }

  @media (min-width: 80em) {
    font-size: 0.7em;
    padding: 0.8em 0;
    width: 100%;
    flex: 1 0 22%;
  }
`;

export const BetInCash = styled(View)`
  width: 100%;
  position: relative;
  max-height: 100%;
  overflow-y: auto;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    color: white;
    flex-basis: 100%;
    flex-shrink: 0;

    ${topGradient};
    ${bottomGradient};

    &:before,
    &:after {
      position: sticky;
    }

    > ${CloseButton} {
      position: fixed;
      top: 1.4em;
      right: 0.9em;
    }

    > h1 {
      font-size: 1.3em;
      margin: 1.7em 0 0;
      padding: 0;
      text-align: center;
    }

    > ul {
      align-self: center;
      justify-items: center;
      list-style: none;
      margin: 1.8em 0;
      padding: 0;

      width: 100%;

      /* No support for grid - IE 11 */
      display: flex;
      flex-wrap: wrap;

      @supports (display: grid) {
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: 0.8em;
      }
    }
  }

  @media (min-width: 48em) and (max-width: 80em) {
    > div {
      > h1 {
        margin-top: 1.5em;
      }

      > ul {
        max-width: 90%;
        grid-template-columns: 1fr 1fr 1fr;
      }
    }
  }

  @media (min-width: 80em) {
    > div {
      > h1 {
        margin-top: 0;
      }

      > ul {
        max-width: 67.2%;
        grid-template-columns: 1fr 1fr 1fr 1fr;
      }
    }
  }
`;
